import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export const Req = () => <span className="text-amber-400"> *</span>;
export const inputCls = "mt-2 bg-slate-950 border-slate-700 text-white";

export function Field({ label, required, hint, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-slate-200">{label}{required && <Req />}</Label>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      {children}
    </div>
  );
}

export function TextField({ id, label, value, onChange, required, type = "text", placeholder, hint, ...rest }) {
  return (
    <Field label={label} required={required} hint={hint}>
      <Input data-testid={`avp-${id}`} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} className={inputCls} {...rest} />
    </Field>
  );
}

export function TextAreaField({ id, label, value, onChange, required, rows = 3, hint }) {
  return (
    <Field label={label} required={required} hint={hint}>
      <Textarea data-testid={`avp-${id}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} className={inputCls} />
    </Field>
  );
}

export function SelectField({ id, label, value, onChange, options, required, placeholder = "Select…", hint }) {
  return (
    <Field label={label} required={required} hint={hint}>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`avp-${id}`} className={inputCls}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 text-white max-h-72">
          {options.map((o) => {
            const [v, l] = Array.isArray(o) ? o : [o, o];
            return <SelectItem key={v} value={v} data-testid={`avp-${id}-opt-${v}`}>{l}</SelectItem>;
          })}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function YesNo({ id, label, value, onChange, required = true, hint, warning, options }) {
  const opts = options || [["yes", "Yes"], ["no", "No"]];
  return (
    <Field label={label} required={required} hint={hint}>
      <RadioGroup value={value || ""} onValueChange={onChange} className="mt-2 flex flex-wrap gap-3">
        {opts.map(([v, l]) => (
          <label key={v} className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 cursor-pointer text-sm transition-colors ${value === v ? "border-amber-500 bg-amber-500/10 text-white" : "border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500"}`}>
            <RadioGroupItem value={v} data-testid={`avp-${id}-${v}`} /> {l}
          </label>
        ))}
      </RadioGroup>
      {warning && value === "no" && <div data-testid={`avp-${id}-warning`} className="mt-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{warning}</div>}
    </Field>
  );
}

export function CheckList({ id, label, value = [], onChange, options, required, hint }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <Field label={label} required={required} hint={hint}>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => {
          const [v, l] = Array.isArray(o) ? o : [o, o];
          const on = value.includes(v);
          return (
            <label key={v} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm transition-colors ${on ? "border-amber-500 bg-amber-500/10 text-white" : "border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500"}`}>
              <Checkbox checked={on} onCheckedChange={() => toggle(v)} data-testid={`avp-${id}-${v}`} /> {l}
            </label>
          );
        })}
      </div>
    </Field>
  );
}

export function SectionIntro({ title, text }) {
  return (
    <div className="mb-6">
      <h2 className="text-2xl md:text-3xl font-bold uppercase display text-white">{title}</h2>
      {text && <p className="mt-2 text-sm text-slate-400 leading-relaxed">{text}</p>}
    </div>
  );
}
